/**
 * @fileoverview Personal Information Component
 *
 * This file implements the final step in the onboarding process where users provide
 * personal demographic information. This component handles country-specific field display,
 * validation rules, and form submission to complete the user profile.
 *
 * Key features:
 * - Dynamic field display based on country of residence
 * - Country-specific validation rules for demographic information
 * - Conditional field rendering based on configuration
 * - Support for self-described identity options
 * - Form validation with error messaging
 * - Final submission to complete the onboarding process
 */

import {
  Button,
  ButtonVariant,
  Select,
  SelectSize,
  Label,
  LabelSize,
  Icon,
  IconName,
  Tooltip,
  ButtonIconAlign,
  TextInput,
  TextInputWidth,
  TextInputSize,
} from "@eightfold.ai/octuple";
import React, { useState, useEffect, useContext } from "react";
import {
  actionButtonStyle,
} from "../../commons/commonStyles";
import dayjs from "dayjs";
import { validateFields, validationOptions } from "../../ValidationSchema";
import { useNavigate } from "react-router";
import { globalContext } from "../../store";
import {
  updateDemographicData,
  checkSubDropDownValues,
  validateDynamicForm,
  ShowStepper,
  resolveError,
  handleDemographicAcknowledge
} from "../../commons/commonFunctions";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import ConfirmationModal from '../../commons/ConfirmationModal';

dayjs.extend(weekday);
dayjs.extend(localeData);

/**
 * Personal Component
 *
 * Renders the personal information step of the onboarding process where users
 * provide demographic information about themselves. The fields displayed vary
 * based on the user's country of residence and client configuration.
 *
 * @param {Object} props - Component props containing form configuration and pre-filled data
 * @returns {JSX.Element} Personal information form
 */
const Personal = (props: any) => {
  const navigate = useNavigate();
  const { globalState, dispatch } = useContext(globalContext);

  // Personal demographic state variables
  const [gender, setGender] = useState("");
  const [ethnicity, setEthnicity] = useState("");
  const [lgbtq, setLgbtq] = useState("");
  const [lgbtqTxt, setLgbtqTxt] = useState("");
  const [diversityNonSpecific, setDiversityNonSpecific] = useState("");
  const [veteranStatus, setVeteranStatus] = useState("");
  const [education, setEducation] = useState("");
  const [countryOfResidence, setCountryOfResidence] = useState("");
  const [age, setAge] = useState<any>("");

  // Form validation and UI state
  const [errors, setErrors] = useState<any>({});
  const [errorMsg, setErrorMsg] = useState(false);
  const toooltipText =
    "Refers to individuals who identify as diverse but either do not fall into one of the above categories (could include but is not limited to veteran status or disability), or do not wish to share further information with the firm as to how they are diverse.";
  const [showLoader, setShowLoader] = useState(false);
  const [showDemographicModal, setShowDemographicModal] = useState(false);
  const [ethnicityOptions, setEthnicityOptions] = useState<any>([]);

  // Field visibility state based on country selection
  const [showEthnicity,] = useState(true);
  const [showGender, setShowGender] = useState(true);
  const [showLGBTQ, setShowLGBTQ] = useState(true);
  const [describeLGBTQ, setDescribeLGBTQ] = useState(true);
  const [showDiversity,] = useState(true);
  const [showVeteran, setShowVeteran] = useState(true);
  const [showAge, setShowAge] = useState(true);
  const [showEducation,] = useState(true);

  // Country-specific configuration constants
  const countryArrEthGenderLgbtqEducationAge = [
    "United States of America (the)",
    "Canada",
    "Australia",
    "United Kingdom of Great Britain and Northern Ireland (the)",
  ];
  const countryArrFRBL = ["France", "Belgium"];
  const selfDescribe = "Prefer to self-describe";
  const countryArrEthGenderLgbtqEducationAgeUsUk = [
    "United States of America (the)",
    "United Kingdom of Great Britain and Northern Ireland (the)",
  ];

  /**
   * Effect hook to initialize form fields with existing data
   *
   * Populates the form fields with data passed through props, typically when
   * a user has previously entered information or is returning to edit
   * their profile information.
   */
  useEffect(() => {
    if (props) {
      if (props?.lgbtq?.includes("~")) {
        setLgbtq(selfDescribe);
        setLgbtqTxt(props?.lgbtq.replace("OTH~", ""));
      } else {
        setLgbtq(props?.lgbtq);
      }
      setGender(props?.gender);
      setEthnicity(props?.ethnicity);
      setDiversityNonSpecific(props?.diversityNonSpecific);
      setVeteranStatus(props?.veteranStatus);
      setEducation(props?.education);
      setCountryOfResidence(props?.countryOfResidence);
      setAge(props?.age);
    }
  }, [props]);
  /**
   * Handles country dropdown change
   *
   * When the country selection changes:
   * 1. Updates the country state
   * 2. Fetches related ethnicity options based on selected country
   * 3. Updates field visibility based on country-specific rules
   * 4. Clears fields that don't apply to the new country selection
   *
   * @param {Object} e - Selected country value from dropdown
   */
  const changeCountry = (e: any) => {
    setCountryOfResidence(e?.[0]);
    const ethnicityOptions = checkSubDropDownValues(
      e?.[0],
      "Ethnicity",
      globalState?.lookUpData,
    );

    setEthnicityOptions(ethnicityOptions);
    showFieldsSpecificToCountry(e?.[0]);
    clearFieldsSpecificToCountry(e?.[0]);
  };

  /**
   * Handles LGBTQ identity dropdown change
   *
   * When the LGBTQ selection changes:
   * 1. Updates the LGBTQ state
   * 2. Shows a text input field if user selects "Prefer to self-describe"
   *
   * @param {Object} e - Selected LGBTQ value from dropdown
   */
  const changeLGBTQ = (e: any) => {
    setLgbtq(e?.[0]);
    if (e?.[0] === selfDescribe) {
      setDescribeLGBTQ(true);
    } else {
      setDescribeLGBTQ(false);
    }
  };

  /**
   * Shows fields specific to selected country
   *
   * Controls which demographic fields are displayed based on the country of residence.
   * Different countries have different legal requirements and cultural norms for
   * collecting demographic information.
   *
   * @param {string} countryName - Selected country name
   */
  const showFieldsSpecificToCountry = (countryName: string) => {
    if (
      countryArrEthGenderLgbtqEducationAgeUsUk.includes(countryName) &&
      typeof countryName !== "undefined"
    ) {
      setShowLGBTQ(true);
    } else {
      setShowLGBTQ(false);
      setDescribeLGBTQ(false);
    }
    // HDLP-475 hide gender and age for countries other than US, UK, Canada and Australia
    if (
      countryArrEthGenderLgbtqEducationAge.includes(countryName) &&
      typeof countryName !== "undefined"
    ) {
      setShowGender(true);
      setShowAge(true);
    } else {
      setShowGender(false);
      setShowAge(false);
    }

    if (countryName === "United States of America (the)") {
      setShowVeteran(true);
    } else {
      setShowVeteran(false);
    }
  };

  /**
   * Clears fields that don't apply to the selected country
   *
   * When changing country of residence, this function resets fields that
   * shouldn't be submitted based on the new country selection to prevent
   * submitting data that's not relevant or allowed for that country.
   *
   * @param {string} countryName - Selected country name
   */
  const clearFieldsSpecificToCountry = (countryName: string) => {
    if (
      !countryArrEthGenderLgbtqEducationAge.includes(countryName) &&
      typeof countryName !== "undefined"
    ) {
      setLgbtq("");
      setLgbtqTxt("");
      setEthnicity("");
      setVeteranStatus("");
      setGender("");
      setAge("");
      setEducation("");
    }

    if (
      countryArrFRBL.includes(countryName) &&
      typeof countryName !== "undefined"
    ) {
      setDiversityNonSpecific("");
    }
  };

  /**
   * Validates LGBTQ and diversity fields
   *
   * Applies validation rules for LGBTQ identity and diversity fields based on
   * the country of residence and which fields are being displayed.
   *
   * @returns {Object} Object containing validation error messages if any
   */
  const validationForLgbtq = () => {
    let lgbtqMsg;
    let diversityNonSpecificMsg;
    let lgbtqDesMsg;

    if (countryArrEthGenderLgbtqEducationAgeUsUk.includes(countryOfResidence)) {
      if (props?.showFields?.showLgbtq) {
        lgbtqMsg = validateFields.validate(
          { dropDown: lgbtq || "" },
          validationOptions,
        )?.error?.details[0]?.message;
      }
      if (lgbtq === selfDescribe) {
        lgbtqDesMsg = validateFields.validate(
          { textField: lgbtqTxt || "" },
          validationOptions,
        )?.error?.details[0]?.message;
      }
    }

    if (
      !countryArrFRBL.includes(countryOfResidence) &&
      props?.showFields?.showDiversityNonSpecific
    ) {
      diversityNonSpecificMsg = validateFields.validate(
        { dropDown: diversityNonSpecific || "" },
        validationOptions,
      )?.error?.details[0]?.message;
    }
    return { lgbtqMsg, diversityNonSpecificMsg, lgbtqDesMsg };
  };
  /**
   * Validates veteran status field for US residents
   *
   * Applies validation rules specifically for the veteran status field
   * which is only shown to US residents.
   *
   * @returns {string|undefined} Validation error message if any
   */
  const veteranMsgCheck = () => {
    let veteranMsg;
    if (
      countryOfResidence === "United States of America (the)" &&
      props?.showFields?.showVeteranStatus
    ) {
      veteranMsg = validateFields.validate(
        { dropDown: veteranStatus || "" },
        validationOptions,
      )?.error?.details[0]?.message;
    }
    return veteranMsg;
  };

  /**
   * Validates ethnicity and gender fields
   *
   * Centralizes all demographic field validation in one place, collecting
   * error messages from specific validation functions.
   *
   * @returns {Object} Object containing all validation error messages
   */
  const validateOptions = () => {
    let ethnicityMsg;
    let genderMsg;
    let ageMsg;
    let educationMsg;
    const { lgbtqMsg, diversityNonSpecificMsg, lgbtqDesMsg } =
      validationForLgbtq();

    if (countryArrEthGenderLgbtqEducationAge.includes(countryOfResidence)) {
      if (props?.showFields?.showEthnicity) {
        ethnicityMsg = validateFields.validate(
          { dropDown: ethnicity || "" },
          validationOptions,
        )?.error?.details[0]?.message;
      }
      if (props?.showFields?.showGender) {
        genderMsg = validateFields.validate(
          { dropDown: gender || "" },
          validationOptions,
        )?.error?.details[0]?.message;
      }

      if (props?.showFields?.showAge) {
        ageMsg = validateFields.validate(
          { dropDown: age || "" },
          validationOptions,
        )?.error?.details[0]?.message;
      }
    }

    if (props?.showFields?.showEducation) {
      educationMsg = validateFields.validate(
        { dropDown: education || "" },
        validationOptions,
      )?.error?.details[0]?.message;
    }

    const veteranMsg = veteranMsgCheck();
    return {
      lgbtqMsg,
      veteranMsg,
      diversityNonSpecificMsg,
      ethnicityMsg,
      genderMsg,
      lgbtqDesMsg,
      ageMsg,
      educationMsg,
    };
  };

  /**
   * Validates the entire form before submission
   *
   * Creates a configuration of all form fields that need validation based on what's
   * displayed in the UI. Applies both standard dropdown validation and custom
   * validation for special fields like the demographic information.
   *
   * @returns {boolean} True if the form has validation errors, false if valid
   */
  const validateFormData = () => {
    const fieldConfigs = [
      {
        name: "showCountryOfResidence",
        value: countryOfResidence,
        type: "dropDown",
      },
    ];

    const filteredFieldConfigs = fieldConfigs?.filter(
      (field) => props?.showFields?.[field.name] === true,
    );
    const customValidations = {
      showLgbtqMsg: () => validateOptions()?.lgbtqMsg,
      showVeteranMsg: () => validateOptions()?.veteranMsg,
      showDiversityNonSpecificMsg: () =>
      validateOptions()?.diversityNonSpecificMsg,
      showEthnicityMsg: () => validateOptions()?.ethnicityMsg,
      showGenderMsg: () => validateOptions()?.genderMsg,
      showLgbtqDesMsg: () => validateOptions()?.lgbtqDesMsg,
      showAgeMsg: () => validateOptions()?.ageMsg,
      showEducationMsg: () => validateOptions()?.educationMsg,
    };
    
    const isFormInvalid = validateDynamicForm(
      filteredFieldConfigs,
      customValidations,
      setErrors,
    );
    return isFormInvalid;
  };

  /**
   * Saves personal data and completes the onboarding process
   *
   * This function:
   * 1. Validates the form data
   * 2. Constructs the payload for API submission
   * 3. Sets isProfileCompleted flag to true
   * 4. Updates the global context with the new data
   * 5. Calls the updateDemographicData function to persist the data
   * 6. Navigates to the dashboard upon successful submission
   */
  const savePersonalData = () => {
    if (validateFormData()) return;

    // Prepare API payload with all personal demographic data
    const payload = {
      isProfileCompleted: true,
      demographicSurveyData: {
        gender: gender,
        ethnicity: ethnicity,
        education: education,
        countryOfResidence: countryOfResidence,
        lgbtq: lgbtq === selfDescribe ? `OTH~${lgbtqTxt}` : lgbtq,
        diversityNonSpecific: diversityNonSpecific,
        veteranStatus: veteranStatus,
        age: age,
      },
    };

    // Prepare data for global context update
    const updatedDataForContext: any = {
      demographicSurveyData: {
        ...globalState?.leaderData?.demographicSurveyData,
        gender: gender,
        ethnicity: ethnicity,
        education: education,
        countryOfResidence: countryOfResidence,
        lgbtq: lgbtq === selfDescribe ? `OTH~${lgbtqTxt}` : lgbtq,
        diversityNonSpecific: diversityNonSpecific,
        veteranStatus: veteranStatus,
        age: age,
      },
    };

    const newContextData = {
      ...globalState?.leaderData,
      ...updatedDataForContext,
      isProfileCompleted: true,
    };
    const redirectUrl = "/dashboard";
    updateDemographicData({
      payload,
      leaderProfileId: props.leaderProfileId,
      redirectUrl,
      setErrorMsg,
      newContextData,
      dispatch,
      navigate,
      setShowLoader,
    });
  };
  /**
   * Navigates back to the previous onboarding step (Company)
   *
   * This function handles the "Back" button click and redirects the user
   * to the previous step in the onboarding flow.
   */
  const backtoCompanyPage = () => {
    const backUrl = props?.isCompanyStepVisible ? '/register-company' :  props?.isRoleStepVisible ? '/register-role' : '/register-account';
    navigate(backUrl);
  };

  /**
   * Effect to show demographic modal on first demographic page
   * Modal appears if this is the first demographic step, demographics are enabled,
   * and the user hasn't acknowledged yet
   */

  const demographicsEnabled = props?.anyDemographicsEnabled === true;
  const isAcknowledged = props?.demographicAcknowledged === true;
  useEffect(() => {
    if (!isAcknowledged && demographicsEnabled) {
      setShowDemographicModal(true);
    }
  }, [isAcknowledged, demographicsEnabled]);

  // Determine if fields should be disabled (when modal hasn't been acknowledged)
  const fieldsDisabled = !isAcknowledged;

  return (
    <div className="accountWrapper">
      <ShowStepper 
        activeIndex={props?.activeIndex} 
        isRoleStepVisible={props?.isRoleStepVisible}
        isCompanyStepVisible={props?.isCompanyStepVisible}
        isPersonalStepVisible={props?.isPersonalStepVisible}
      />
      <h2 className="onboardingTitle">Personal Details</h2>
      {props?.countryOfResidenceOptions?.length > 0 && (
        <>
          {/* Country of Residence dropdown field - this is always displayed */}
          {props?.showFields?.showCountryOfResidence && (
            <div className="fieldWrap">
              <Label
                htmlFor=""
                classNames="labelText"
                size={LabelSize.Medium}
                text="What is your country of residence?"
              />
              <Select
                onOptionsChange={(e: any) => {
                  changeCountry(e);
                  resolveError('showCountryOfResidence', errors, setErrors);
                }}
                filterable
                options={props?.countryOfResidenceOptions}
                classNames="selectField"
                size={SelectSize.Large}
                defaultValue={props?.countryOfResidence}
                status={errors?.showCountryOfResidenceStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showCountryOfResidenceMsg && (
                <span className="errorMessage">
                  {errors?.showCountryOfResidenceMsg}
                </span>
              )}
            </div>
          )}

          {/* Ethnicity dropdown - only shown for specific countries */}
          {ethnicityOptions?.length > 0 &&
            showEthnicity &&
            props?.showFields?.showEthnicity && (
              <div className="fieldWrap">
                <Label
                  htmlFor=""
                  classNames="labelText"
                  size={LabelSize.Medium}
                  text="What is your ethnicity?"
                />
                <Select
                  onOptionsChange={(e: any) => {
                    setEthnicity(e?.[0]);
                    resolveError('showEthnicity', errors, setErrors);
                  }}
                  options={ethnicityOptions}
                  classNames="selectField"
                  size={SelectSize.Large}
                  defaultValue={props?.ethnicity}
                  status={errors?.showEthnicityStatus}
                  disabled={fieldsDisabled}
                />
                {errors?.showEthnicityMsg && (
                  <span className="errorMessage">
                    {errors?.showEthnicityMsg}
                  </span>
                )}
              </div>
            )}

          {/* Gender dropdown - only shown for specific countries */}
          {showGender && props?.showFields?.showGender && (
            <div className="fieldWrap">
              <Label
                htmlFor=""
                classNames="labelText"
                size={LabelSize.Medium}
                text="What is your gender?"
              />
              <Select
                onOptionsChange={(e: any) => {
                  setGender(e?.[0]);
                  resolveError('showGender', errors, setErrors);
                }}
                options={props?.genderOptions}
                classNames="selectField"
                size={SelectSize.Large}
                defaultValue={props?.gender}
                status={errors?.showGenderStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showGenderMsg && (
                <span className="errorMessage">{errors?.showGenderMsg}</span>
              )}
            </div>
          )}

          {/* LGBTQ identity dropdown - only shown for specific countries */}
          {showLGBTQ && props?.showFields?.showLgbtq && (
            <div className="fieldWrap">
              <Label
                htmlFor=""
                classNames="labelText"
                size={LabelSize.Medium}
                text="How do you identify?"
              />
              <Select
                onOptionsChange={(e: any) => {
                  changeLGBTQ(e);
                  resolveError('showLgbtq', errors, setErrors);
                }}
                options={props?.lgbtqOptions}
                classNames="selectField"
                size={SelectSize.Large}
                defaultValue={lgbtq}
                status={errors?.showLgbtqStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showLgbtqMsg && (
                <span className="errorMessage">{errors?.showLgbtqMsg}</span>
              )}
            </div>
          )}

          {/* Custom LGBTQ description field - only shown when "Prefer to self-describe" is selected */}
          {describeLGBTQ && (
            <div className="fieldWrap">
              <TextInput
                htmlType="text"
                id="LGBTQDescribe"
                clearable
                maxlength={100}
                minlength={0}
                name="LGBTQDescribe"
                onChange={(e) => {
                  setLgbtqTxt(e.target.value);
                  resolveError('showLgbtqDes', errors, setErrors);
                }}
                labelProps={{
                  classNames: "labelText",
                }}
                value={lgbtqTxt}
                defaultValue={lgbtqTxt}
                inputWidth={TextInputWidth.fill}
                classNames="inputTextField"
                size={TextInputSize.Large}
                status={errors?.showLgbtqDesMsg ? "error" : "validating"}
              />
              {errors?.showLgbtqDesMsg && (
                <span className="errorMessage">{errors?.showLgbtqDesMsg}</span>
              )}
            </div>
          )}

          {/* Diversity identification dropdown - shown for countries outside France and Belgium */}
          {showDiversity && props?.showFields?.showDiversityNonSpecific && (
            <div className="fieldWrap">
              <div className="tooltipText">
                <Label
                  htmlFor=""
                  classNames="labelText"
                  size={LabelSize.Medium}
                  text="Do you identify as being a part of any underrepresented group?"
                />
                <Tooltip
                  animate
                  classNames="my-tooltip-class"
                  content={toooltipText}
                  hideAfter={200}
                  id="myTooltipId"
                  offset={8}
                  openDelay={0}
                  placement="bottom"
                  positionStrategy="absolute"
                  tabIndex={-1}
                  trigger="hover"
                  visibleArrow>
                  {/* <img src={'../assets/img/materialIcon.svg'} alt='' /> */}
                  <Icon path={IconName.mdiInformation} />
                </Tooltip>
              </div>
              <Select
                onOptionsChange={(e: any) => {
                  setDiversityNonSpecific(e?.[0]);
                  resolveError('showDiversityNonSpecific', errors, setErrors);
                }}
                options={props?.diversityNonSpecificOptions}
                classNames="selectField"
                size={SelectSize.Large}
                defaultValue={props?.diversityNonSpecific}
                status={errors?.showDiversityNonSpecificStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showDiversityNonSpecificMsg && (
                <span className="errorMessage">
                  {errors?.showDiversityNonSpecificMsg}
                </span>
              )}
            </div>
          )}

          {/* Age dropdown field */}
          {showAge && props?.showFields?.showAge && (
            <div className="fieldWrap">
              <Label
                htmlFor=""
                classNames="labelText"
                size={LabelSize.Medium}
                text="What is your age?"
              />
              <Select
                onOptionsChange={(e: any) => {
                  setAge(e?.[0]);
                  resolveError('showAge', errors, setErrors);
                }}
                options={props?.ageOptions}
                classNames="selectField"
                size={SelectSize.Large}
                defaultValue={props?.age}
                status={errors?.showAgeStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showAgeMsg && (
                <span className="errorMessage">{errors?.showAgeMsg}</span>
              )}
            </div>
          )}

          {/* Veteran status dropdown - only shown for US residents */}
          {showVeteran && props?.showFields?.showVeteranStatus && (
            <div className="fieldWrap">
              <Label
                htmlFor=""
                classNames="labelText"
                size={LabelSize.Medium}
                text="Did you serve on active duty in the U.S. Military?"
              />
              <Select
                onOptionsChange={(e: any) => {
                  setVeteranStatus(e?.[0]);
                  resolveError('showVeteran', errors, setErrors);
                }}
                options={props?.veteranStatusOptions}
                classNames="selectField"
                size={SelectSize.Large}
                defaultValue={props?.veteranStatus}
                status={errors?.showVeteranStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showVeteranMsg && (
                <span className="errorMessage">{errors?.showVeteranMsg}</span>
              )}
            </div>
          )}

          {/* Education level dropdown field */}
          {showEducation && props?.showFields?.showEducation && (
            <div className="fieldWrap">
              <Label
                htmlFor=""
                classNames="labelText"
                size={LabelSize.Medium}
                text="What is the highest level of education you have completed?"
              />
              <Select
                onOptionsChange={(e: any) => {
                  setEducation(e?.[0]);
                  resolveError('showEducation', errors, setErrors);
                }}
                options={props?.educationOptions}
                classNames="selectField"
                size={SelectSize.Large}
                defaultValue={props?.education}
                status={errors?.showEducationStatus}
                disabled={fieldsDisabled}
              />
              {errors?.showEducationMsg && (
                <span className="errorMessage">{errors?.showEducationMsg}</span>
              )}
            </div>
          )}

          {/* Form navigation buttons */}
          <div className="fieldWrap-Btn-multiple">
            {/* Back button with left chevron icon */}
            <Button
              text="Previous"
              variant={ButtonVariant.Secondary}
              classNames="backButton"
              onClick={backtoCompanyPage}
              transparent={true}
              iconProps={{ path: IconName.mdiChevronLeft }}
              alignIcon={ButtonIconAlign.Left}
              disabled={fieldsDisabled}
            />
            {/* Submit button with loading state */}
            <Button
              text="Submit"
              variant={ButtonVariant.Secondary}
              classNames="actionButton"
              style={actionButtonStyle}
              onClick={() => {
                savePersonalData();
              }}
              iconProps={
                showLoader
                  ? { path: IconName.mdiLoading, spin: true }
                  : { path: IconName.mdiArrowRight }
              }
              alignIcon={ButtonIconAlign.Right}
              disabled={showLoader || fieldsDisabled}
            />
          </div>
        </>
      )}
      {/* API error message */}
      {errorMsg && <span className="apiErrorMessage">An error occurred</span>}
      {showDemographicModal && (
        <ConfirmationModal
          header='How we use your responses'
          showModal={showDemographicModal}
          bodyText='The following demographic questions are voluntary and will not be used in the scoring of your assessment or in any employment, promotion, or development decisions. This information is collected solely for research, validation, and fairness monitoring purposes to help ensure the assessment functions consistently and equitably across different groups. Responses will be analyzed in aggregate and handled in accordance with applicable privacy and data protection requirements.'
          showCancelButton={false}
          onContinue={() => handleDemographicAcknowledge(dispatch, setShowDemographicModal)}
          continueBtnTxt={'I understand'}
          onClose={() => setShowDemographicModal(false)}
          isAcknowledge={true}
          maskClosable={false}
        />
      )}
    </div>
  );
};

export default Personal;
